import { useEffect, useState } from "react";
import axios from "axios";
import {
  Award, AlertCircle, ListChecks, Sparkles, Target,
  CheckCircle2, Printer, FileText, Download, Loader2,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * PkiMonatsreport · 4-Wochen-Management-Report mit Sparklines + Druckansicht.
 *
 * Standalone-Komponente — kann im Dashboard eingebunden oder über eigene Route
 * angesteuert werden. Druck-Button nutzt window.print() mit speziellem CSS.
 */
export default function PkiMonatsreport({ weeks = 4 }) {
  const [report, setReport] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pdfBusy, setPdfBusy] = useState(false);

  const downloadPdf = async () => {
    setPdfBusy(true);
    try {
      const r = await axios.get(`${API}/pflege-ki/monatsreport/pdf?weeks=${weeks}`, {
        withCredentials: true,
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `Monatsreport-Pflege-KI-${new Date().toISOString().slice(0, 10)}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("PDF-Report heruntergeladen.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "PDF-Erzeugung fehlgeschlagen.");
    } finally {
      setPdfBusy(false);
    }
  };

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/pflege-ki/monatsreport?weeks=${weeks}`, { withCredentials: true });
        setReport(r.data);
      } catch (e) {
        setError(e?.response?.data?.detail || "Monatsreport nicht verfügbar.");
      } finally {
        setLoading(false);
      }
    })();
  }, [weeks]);

  if (loading) return <p className="text-[13px] text-sand-500 italic">Lade Monatsreport…</p>;
  if (error) return <p className="text-[13px] text-brand-darkred" data-testid="monatsreport-error">{error}</p>;
  if (!report) return null;

  const es = report.executive_summary || {};
  const qe = report.qualitaetsentwicklung || {};
  const fazit = report.management_fazit || {};
  const ampelColor = fazit.ampel === "gruen" ? "bg-sage-dark"
    : fazit.ampel === "gelb" ? "bg-saffron"
    : "bg-brand-darkred";

  return (
    <article
      data-testid="monatsreport"
      className="space-y-6 print:space-y-3 print:text-[12px]"
    >
      {/* Print-CSS · versteckt Buttons + scaled Layout für Druck */}
      <style>{`
        @media print {
          [data-no-print] { display: none !important; }
          [data-testid="monatsreport"] { color: #000 !important; }
          aside, nav, header.app-header { display: none !important; }
        }
      `}</style>

      {/* Print-Button + PDF-Download */}
      <div className="flex justify-end gap-2 flex-wrap" data-no-print>
        <Button
          data-testid="monatsreport-print"
          onClick={() => window.print()}
          variant="outline"
          className="border-clay-dark/40 text-clay-dark"
        >
          <Printer className="h-4 w-4 mr-2" /> Drucken
        </Button>
        <Button
          data-testid="monatsreport-pdf"
          onClick={downloadPdf}
          disabled={pdfBusy}
          className="bg-clay-dark hover:bg-clay text-white"
        >
          {pdfBusy
            ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> PDF wird erzeugt …</>
            : <><Download className="h-4 w-4 mr-2" /> PDF herunterladen</>}
        </Button>
      </div>

      {/* 1 · Executive Summary */}
      <section className="rounded-2xl border border-clay-dark/30 bg-cream-50 p-5 md:p-6" data-testid="monatsreport-summary">
        <header className="flex items-center gap-2 mb-3">
          <FileText className="h-5 w-5 text-clay-dark" />
          <h2 className="text-[14px] uppercase tracking-[0.18em] font-bold text-clay-dark">
            Executive Summary · {report.berichtszeitraum_wochen} Wochen
          </h2>
        </header>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <SummaryStat icon={Award} label="QPR-Readiness Ø" value={`${es.qpr_readiness_avg || 0} %`} testid="ms-qpr" />
          <SummaryStat icon={AlertCircle} label="Auffälligkeiten" value={es.auffaelligkeiten_gesamt || 0} testid="ms-auff" accent="terra" />
          <SummaryStat icon={Sparkles} label="Halluz-Eingriffe" value={es.halluzinationsschutz_eingriffe || 0} testid="ms-halluz" accent="sage-dark" />
          <SummaryStat icon={ListChecks} label="Cross-Doc-Widersprüche" value={es.cross_doc_widersprueche || 0} testid="ms-xdoc" />
        </div>
        {es.wichtigste_verbesserung && (
          <p className="mt-4 text-[13px] text-sand-700 italic" data-testid="ms-verbesserung">
            Wichtigste Verbesserung: {es.wichtigste_verbesserung}
          </p>
        )}
      </section>

      {/* 2 · Qualitätsentwicklung mit Sparklines */}
      <section className="rounded-2xl border border-cream-300 bg-white p-5 md:p-6" data-testid="monatsreport-sparklines">
        <h2 className="text-[14px] uppercase tracking-[0.14em] font-bold text-sand-700 mb-4">
          Qualitätsentwicklung · {qe.wochen?.join(" → ")}
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <SparkCard title="QPR-Readiness" data={qe.sparklines?.qpr} unit="%" lowerIsBetter={false} testid="sparkline-qpr" />
          <SparkCard title="Auffälligkeiten" data={qe.sparklines?.auffaelligkeiten} lowerIsBetter testid="sparkline-auff" />
          <SparkCard title="Cross-Doc-Widersprüche" data={qe.sparklines?.xdoc} lowerIsBetter testid="sparkline-xdoc" />
          <SparkCard title="Halluz-Eingriffe" data={qe.sparklines?.halluz_eingriffe} lowerIsBetter={false} testid="sparkline-halluz" />
        </div>
      </section>

      {/* 3 + 4 · Top-3 Risiken + Verbesserungen */}
      <section className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="rounded-xl border border-brand-darkred/30 bg-brand-darkred/5 p-4" data-testid="monatsreport-risiken">
          <h3 className="text-[12.5px] uppercase tracking-[0.14em] font-bold text-brand-darkred mb-3">
            Top-3 Risiken
          </h3>
          {report.top3_risiken?.length > 0 ? (
            <ol className="space-y-2.5">
              {report.top3_risiken.map((r, i) => (
                <li key={i} className="text-[13px] text-sand-800">
                  <p className="font-bold">{i + 1}. {r.risiko}</p>
                  <p className="text-[12px] text-sand-600">
                    {r.betroffene_klienten} Klient:innen betroffen
                  </p>
                  <p className="text-[12px] text-sage-dark mt-0.5">→ {r.handlungsempfehlung}</p>
                </li>
              ))}
            </ol>
          ) : <p className="text-[12.5px] text-sand-500 italic">Keine kritischen Risiken.</p>}
        </div>

        <div className="rounded-xl border border-sage-dark/30 bg-sage-dark/5 p-4" data-testid="monatsreport-verbesserungen">
          <h3 className="text-[12.5px] uppercase tracking-[0.14em] font-bold text-sage-dark mb-3">
            Top-3 Verbesserungen
          </h3>
          {report.top3_verbesserungen?.length > 0 ? (
            <ol className="space-y-2.5">
              {report.top3_verbesserungen.map((v, i) => (
                <li key={i} className="text-[13px] text-sand-800">
                  <p className="font-bold">{i + 1}. {v.was_verbessert}</p>
                  <p className="text-[12px] text-sage-dark">{v.messbarer_effekt}</p>
                </li>
              ))}
            </ol>
          ) : <p className="text-[12.5px] text-sand-500 italic">In diesem Berichtszeitraum noch keine messbaren Verbesserungen erfasst.</p>}
        </div>
      </section>

      {/* 5 · Coaching-Plan Folgemonat */}
      {report.coaching_folgemonat?.length > 0 && (
        <section className="rounded-2xl border border-terra/35 bg-terra/8 p-5" data-testid="monatsreport-coaching">
          <h3 className="text-[12.5px] uppercase tracking-[0.14em] font-bold text-terra mb-3 flex items-center gap-2">
            <Target className="h-4 w-4" /> Coaching-Plan Folgemonat
          </h3>
          <ol className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {report.coaching_folgemonat.map((c, i) => (
              <li key={i} className="rounded-lg bg-white border border-cream-300 p-3" data-testid={`monatsreport-coaching-item-${i}`}>
                <p className="text-[11.5px] uppercase tracking-[0.12em] font-bold text-terra">
                  Priorität {c.prio}
                </p>
                <p className="text-[13.5px] font-bold text-sand-800 mt-1 leading-snug">{c.titel}</p>
                <p className="text-[12px] text-sand-600 mt-1">{c.begruendung}</p>
              </li>
            ))}
          </ol>
        </section>
      )}

      {/* 6 · Management-Fazit · Ampel */}
      <section className="rounded-2xl border border-cream-300 bg-white p-5 md:p-6 flex items-start gap-4" data-testid="monatsreport-fazit">
        <div className={`w-3.5 h-3.5 rounded-full ${ampelColor} mt-1.5 shrink-0`} data-testid="monatsreport-ampel" />
        <div>
          <p className="text-[11.5px] uppercase tracking-[0.18em] font-bold text-sand-600">
            Management-Fazit · MD-/QPR-Bereitschaft
          </p>
          <p className="font-display text-xl md:text-2xl font-semibold text-sand-800 mt-1 leading-snug" data-testid="monatsreport-fazit-text">
            {fazit.fazit}
          </p>
        </div>
      </section>
    </article>
  );
}

function SummaryStat({ icon: Icon, label, value, testid, accent }) {
  const color = accent === "terra" ? "text-terra"
    : accent === "sage-dark" ? "text-sage-dark"
    : "text-sand-800";
  return (
    <div className="rounded-xl bg-white border border-cream-300 p-3" data-testid={testid}>
      <div className="flex items-center gap-1.5">
        <Icon className="h-3.5 w-3.5 text-sand-500" />
        <span className="text-[10px] uppercase tracking-[0.14em] font-bold text-sand-500">{label}</span>
      </div>
      <p className={`mt-1.5 text-2xl font-display font-semibold ${color} tabular-nums leading-none`}>{value}</p>
    </div>
  );
}

function SparkCard({ title, data, unit = "", lowerIsBetter, testid }) {
  if (!data || !Array.isArray(data.values) || data.values.length === 0) {
    return (
      <div className="rounded-xl border border-cream-300 bg-cream-50/50 p-3" data-testid={testid}>
        <p className="text-[11px] uppercase tracking-[0.12em] font-bold text-sand-500">{title}</p>
        <p className="text-[12px] text-sand-500 italic mt-2">Keine Daten</p>
      </div>
    );
  }
  const w = 160, h = 48, pad = 4;
  const min = Math.min(...data.values, 0);
  const max = Math.max(...data.values, 1);
  const range = max - min || 1;
  const pts = data.values.map((v, i) => {
    const x = pad + (i / (data.values.length - 1 || 1)) * (w - 2 * pad);
    const y = h - pad - ((v - min) / range) * (h - 2 * pad);
    return [x, y];
  });
  const path = pts.map((p, i) => (i === 0 ? "M" : "L") + p[0].toFixed(1) + "," + p[1].toFixed(1)).join(" ");
  const last = pts[pts.length - 1];

  const trend = data.trend_4w || 0;
  let trendColor = "text-sand-500";
  if (trend !== 0) {
    const positive = lowerIsBetter ? trend < 0 : trend > 0;
    trendColor = positive ? "text-sage-dark" : "text-brand-darkred";
  }
  const arrow = trend > 0 ? "▲" : trend < 0 ? "▼" : "=";

  return (
    <div className="rounded-xl border border-cream-300 bg-white p-3" data-testid={testid}>
      <p className="text-[10.5px] uppercase tracking-[0.12em] font-bold text-sand-600">{title}</p>
      <div className="flex items-baseline gap-2 mt-1">
        <span className="text-2xl font-display font-semibold text-sand-800 tabular-nums">
          {Math.round(data.current * 10) / 10}{unit}
        </span>
        <span className={`text-[12px] font-bold ${trendColor}`}>
          {arrow} {Math.abs(Math.round(trend * 10) / 10)}{unit}
        </span>
      </div>
      <svg viewBox={`0 0 ${w} ${h}`} className="mt-1 w-full h-12">
        <path d={path} fill="none" stroke="currentColor" className="text-clay-dark" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
        <circle cx={last[0]} cy={last[1]} r="2.6" className="fill-terra" />
      </svg>
    </div>
  );
}
